// =============================================================================
// src/features/analytics/analytics-chart-controller.ts - Interacoes do grafico.
// =============================================================================

import { qs, qsa } from '../../app/dom';
import { showMiniToast } from '../../chrome/mini-toast/index';
import {
  type OmarAnalyticsChartGranularity,
  type OmarAnalyticsChartMode,
} from './analytics-data';
import {
  analyticsChartSampleFor,
  analyticsChartSeriesFor,
  buildAnalyticsChartInspectorMarkup,
  buildAnalyticsChartStageMarkup,
} from './analytics-chart-markup';

/** Liga tabs, granularidade e pontos amostrais dos graficos. */
export function bindAnalyticsChartControls(): void {
  qsa<HTMLElement>('[data-analytics-chart-panel]').forEach(bindAnalyticsChartPanel);
}

function bindAnalyticsChartPanel(chartPanelEl: HTMLElement): void {
  qsa<HTMLButtonElement>('[data-analytics-chart-mode]', chartPanelEl).forEach(bindAnalyticsChartModeButton);
  qsa<HTMLSelectElement>('[data-analytics-granularity]', chartPanelEl).forEach(bindAnalyticsGranularitySelect);
  bindAnalyticsChartSamples(chartPanelEl);
}

function bindAnalyticsChartModeButton(chartModeButton: HTMLButtonElement): void {
  chartModeButton.addEventListener('click', () => switchAnalyticsChartMode(chartModeButton));
}

function bindAnalyticsGranularitySelect(granularitySelect: HTMLSelectElement): void {
  granularitySelect.addEventListener('change', () => changeAnalyticsChartGranularity(granularitySelect));
}

function bindAnalyticsChartSamples(chartPanelEl: HTMLElement): void {
  qsa<HTMLButtonElement>('[data-analytics-chart-sample]', chartPanelEl).forEach((sampleButton) => {
    sampleButton.addEventListener('click', () => selectAnalyticsChartSample(sampleButton));
  });
}

function switchAnalyticsChartMode(chartModeButton: HTMLButtonElement): void {
  const chartMode = parseAnalyticsChartMode(chartModeButton.dataset.analyticsChartMode);
  const chartPanelEl = chartModeButton.closest<HTMLElement>('[data-analytics-chart-panel]');
  if (!chartMode || !chartPanelEl) return;
  setAnalyticsChartButtons(chartPanelEl, chartMode);
  setAnalyticsChartViews(chartPanelEl, chartMode);
  updateAnalyticsChartInspector(chartPanelEl, chartMode);
}

function changeAnalyticsChartGranularity(granularitySelect: HTMLSelectElement): void {
  const chartPanelEl = granularitySelect.closest<HTMLElement>('[data-analytics-chart-panel]');
  const granularity = parseAnalyticsGranularity(granularitySelect.value);
  if (!chartPanelEl || !granularity) return;
  renderAnalyticsChartStage(chartPanelEl, granularity);
  showMiniToast(`Grafico reamostrado: ${granularitySelect.selectedOptions[0]?.text ?? granularitySelect.value}`, 'ti-chart-line');
}

function renderAnalyticsChartStage(chartPanelEl: HTMLElement, granularity: OmarAnalyticsChartGranularity): void {
  const activeMode = activeAnalyticsChartMode(chartPanelEl);
  qs<HTMLElement>('[data-analytics-chart-stage]', chartPanelEl)?.remove();
  qs<HTMLElement>('.u-analytics-panel-head', chartPanelEl)?.insertAdjacentHTML('afterend', buildAnalyticsChartStageMarkup(granularity, activeMode));
  bindAnalyticsChartSamples(chartPanelEl);
}

function selectAnalyticsChartSample(sampleButton: HTMLButtonElement): void {
  const chartPanelEl = sampleButton.closest<HTMLElement>('[data-analytics-chart-panel]');
  if (!chartPanelEl) return;
  setAnalyticsSampleButtons(chartPanelEl, sampleButton.dataset.analyticsChartSample);
  updateAnalyticsChartInspector(chartPanelEl, activeAnalyticsChartMode(chartPanelEl));
}

function setAnalyticsChartButtons(chartPanelEl: HTMLElement, chartMode: OmarAnalyticsChartMode): void {
  qsa<HTMLButtonElement>('[data-analytics-chart-mode]', chartPanelEl).forEach((buttonEl) => {
    const isActive = buttonEl.dataset.analyticsChartMode === chartMode;
    buttonEl.classList.toggle('is-active', isActive);
    buttonEl.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
}

function setAnalyticsChartViews(chartPanelEl: HTMLElement, chartMode: OmarAnalyticsChartMode): void {
  qsa<HTMLElement>('[data-analytics-chart-view]', chartPanelEl).forEach((chartViewEl) => {
    chartViewEl.hidden = chartViewEl.dataset.analyticsChartView !== chartMode;
  });
}

function setAnalyticsSampleButtons(chartPanelEl: HTMLElement, activeIndex: string | undefined): void {
  qsa<HTMLButtonElement>('[data-analytics-chart-sample]', chartPanelEl).forEach((buttonEl) => {
    buttonEl.classList.toggle('is-active', buttonEl.dataset.analyticsChartSample === activeIndex);
  });
}

function updateAnalyticsChartInspector(chartPanelEl: HTMLElement, chartMode: OmarAnalyticsChartMode): void {
  const sampleIndex = activeAnalyticsSampleIndex(chartPanelEl);
  const chartSeries = analyticsChartSeriesFor(activeAnalyticsGranularity(chartPanelEl));
  const chartSample = analyticsChartSampleFor(chartSeries, sampleIndex);
  qs<HTMLElement>('[data-analytics-chart-inspector]', chartPanelEl)?.remove();
  qs<HTMLElement>('[data-analytics-chart-stage]', chartPanelEl)?.insertAdjacentHTML('beforeend', buildAnalyticsChartInspectorMarkup(chartSample, chartMode));
}

function activeAnalyticsSampleIndex(chartPanelEl: HTMLElement): number {
  const activeButton = qs<HTMLButtonElement>('[data-analytics-chart-sample].is-active', chartPanelEl);
  return Number(activeButton?.dataset.analyticsChartSample ?? '0');
}

function activeAnalyticsGranularity(chartPanelEl: HTMLElement): OmarAnalyticsChartGranularity {
  const stageEl = qs<HTMLElement>('[data-analytics-chart-stage]', chartPanelEl);
  return parseAnalyticsGranularity(stageEl?.dataset.analyticsChartGranularity) ?? 'days';
}

function activeAnalyticsChartMode(chartPanelEl: HTMLElement): OmarAnalyticsChartMode {
  const activeButton = qs<HTMLButtonElement>('[data-analytics-chart-mode].is-active', chartPanelEl);
  return parseAnalyticsChartMode(activeButton?.dataset.analyticsChartMode) ?? 'reach';
}

function parseAnalyticsGranularity(rawValue: string | undefined): OmarAnalyticsChartGranularity | null {
  if (rawValue === 'days' || rawValue === 'weeks' || rawValue === 'months') return rawValue;
  return null;
}

function parseAnalyticsChartMode(rawValue: string | undefined): OmarAnalyticsChartMode | null {
  if (rawValue === 'reach' || rawValue === 'sentiment') return rawValue;
  return null;
}
